import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

type ToastTone = "info" | "success" | "danger";

interface ToastItem {
  id: number;
  message: string;
  tone: ToastTone;
}

interface ToastContextValue {
  show: (message: string, tone?: ToastTone) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const TONE_STYLE: Record<ToastTone, { background: string; color: string; border: string }> = {
  info: { background: "var(--color-panel)", color: "var(--color-text)", border: "var(--color-border)" },
  success: {
    background: "var(--color-success)",
    color: "var(--color-success-foreground)",
    border: "var(--color-success)",
  },
  danger: {
    background: "var(--color-danger)",
    color: "var(--color-danger-foreground)",
    border: "var(--color-danger)",
  },
};

let nextId = 1;

/** Mount once near the app root. Provides useToast() to the whole tree. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const show = useCallback((message: string, tone: ToastTone = "info") => {
    const id = nextId++;
    setItems((prev) => [...prev, { id, message, tone }]);
    setTimeout(() => {
      setItems((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  const value = useMemo(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        style={{
          position: "fixed",
          top: "var(--space-5)",
          right: "var(--space-5)",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: "var(--space-2)",
          zIndex: 2000,
        }}
      >
        {items.map((item) => (
          <div
            key={item.id}
            role="status"
            style={{
              background: TONE_STYLE[item.tone].background,
              color: TONE_STYLE[item.tone].color,
              border: `1px solid ${TONE_STYLE[item.tone].border}`,
              boxShadow: "var(--shadow-lg)",
              borderRadius: "var(--radius-md)",
              padding: "var(--space-3) var(--space-4)",
              fontSize: "var(--font-size-sm)",
              fontWeight: "var(--font-weight-medium)",
              maxWidth: "calc(var(--space-8) * 7)",
              textAlign: "left",
            }}
          >
            {item.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast() must be used within a ToastProvider");
  return ctx;
}
